import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, QrCode, CreditCard } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const QR_ENABLED = process.env.REACT_APP_CHARI_QR_ENABLED === 'true';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * One-screen checkout: QR (Maroc Pay via Chari Pay) on top, card (CMI) below.
 * URL: /payment/:quoteId?email=...&amount=...
 */
const PaymentRedirectPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quoteId = window.location.pathname.split('/').pop();
  const email = searchParams.get('email');
  const amountParam = searchParams.get('amount');

  const [error, setError] = useState('');
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [cmiForm, setCmiForm] = useState(null);       // CMI hidden-form data
  const [cmiLoading, setCmiLoading] = useState(false);
  const [qr, setQr] = useState(null);                 // { qr_payload, qr_reference, payment_id, amount }
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');
  const formRef = useRef(null);

  useEffect(() => {
    if (!quoteId || !email) {
      setError('Missing quote ID or email. Please go back to your quotes page.');
    }
  }, [quoteId, email]);

  // Card (CMI) — initiate then auto-submit the hidden form to the CMI gateway.
  const payWithCard = async () => {
    setCmiLoading(true);
    setError('');
    try {
      const returnUrl = `${window.location.origin}/payment-result`;
      const { data } = await axios.post(`${API}/payments/initiate`, {
        quote_id: quoteId,
        customer_email: email,
        web_return_url: returnUrl,
      });
      setCmiForm(data);
    } catch (err) {
      if (err.response?.status === 409) setAlreadyPaid(true);
      else setError(err.response?.data?.detail || 'Could not start card payment. Please try again.');
      setCmiLoading(false);
    }
  };

  useEffect(() => {
    if (cmiForm && formRef.current) formRef.current.submit();
  }, [cmiForm]);

  // QR (Chari / Maroc Pay) — create a dynamic QR then poll for completion.
  const payWithQr = async () => {
    setQrLoading(true);
    setQrError('');
    try {
      const { data } = await axios.post(`${API}/payments/chari/qr`, {
        quote_id: quoteId,
        customer_email: email,
      });
      setQr(data);
    } catch (err) {
      if (err.response?.status === 409) setAlreadyPaid(true);
      else setQrError(
        'QR payment is not available right now. Please pay by card below, or try again shortly.'
      );
    } finally {
      setQrLoading(false);
    }
  };

  const checkQrStatus = useCallback(async () => {
    if (!qr?.payment_id) return;
    try {
      const { data } = await axios.get(`${API}/payments/chari/status/${qr.payment_id}`);
      if (data.status === 'completed') {
        navigate(`/payment-result?status=success&payment_id=${qr.payment_id}`);
      } else if (data.status === 'failed') {
        setQrError('The QR payment failed or was cancelled. Please try again or pay by card.');
        setQr(null);
      }
    } catch (_e) { /* keep polling */ }
  }, [qr, navigate]);

  useEffect(() => {
    if (!qr?.payment_id) return undefined;
    const id = window.setInterval(checkQrStatus, 3000);
    return () => window.clearInterval(id);
  }, [qr, checkQrStatus]);

  const displayAmount = qr?.amount ?? (amountParam ? Number(amountParam) : null);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Navbar />
      <section className="flex-1 flex items-center justify-center px-4 pt-24 pb-12">
        <div className="max-w-md w-full bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-10 text-center" data-testid="payment-checkout">
          {error ? (
            <>
              <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Error</h1>
              <p className="text-gray-400 mb-6" data-testid="payment-error-message">{error}</p>
              <button onClick={() => navigate(-1)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 px-8 rounded-full">
                Go Back
              </button>
            </>
          ) : alreadyPaid ? (
            <>
              <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" data-testid="already-paid-heading">Already Paid</h1>
              <p className="text-gray-400 mb-8">This booking has already been paid for. No further action is needed.</p>
              <button onClick={() => navigate('/dashboard')} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 rounded-full" data-testid="already-paid-back-btn">
                Back to My Dashboard
              </button>
            </>
          ) : cmiForm ? (
            <>
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Redirecting to secure payment</h1>
              <p className="text-gray-400">Please wait while we connect you to CMI...</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white mb-1">Complete your payment</h1>
              {displayAmount != null && (
                <p className="text-amber-400 font-mono text-lg mb-6" data-testid="checkout-amount">MAD {displayAmount.toFixed(2)}</p>
              )}

              {/* QR (Maroc Pay) — top; parked behind REACT_APP_CHARI_QR_ENABLED */}
              {QR_ENABLED && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4 text-left" data-testid="qr-payment-section">
                <div className="flex items-center gap-2 mb-3">
                  <QrCode className="w-5 h-5 text-emerald-400" />
                  <h2 className="text-white font-semibold text-base">Pay by QR — Maroc Pay</h2>
                </div>
                {qr ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-white p-3 rounded-xl" data-testid="qr-code-image">
                      <QRCodeCanvas value={qr.qr_payload} size={220} includeMargin />
                    </div>
                    <p className="text-gray-300 text-sm text-center">
                      Scan with your bank / Maroc Pay app to pay.
                    </p>
                    <p className="text-gray-500 text-xs">Ref: {qr.qr_reference}</p>
                    <div className="flex items-center gap-2 text-emerald-400 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Waiting for confirmation…
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-400 text-sm mb-3">Instant payment by scanning a QR code with your mobile banking app.</p>
                    <button
                      onClick={payWithQr}
                      disabled={qrLoading}
                      data-testid="pay-qr-btn"
                      className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                    >
                      {qrLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <QrCode className="w-5 h-5" />}
                      Show QR code
                    </button>
                    {qrError && <p className="text-red-400 text-xs mt-2" data-testid="qr-error">{qrError}</p>}
                  </>
                )}
              </div>
              )}

              {/* Card (CMI) — below */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-left" data-testid="card-payment-section">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-5 h-5 text-amber-400" />
                  <h2 className="text-white font-semibold text-base">Pay by card</h2>
                </div>
                <p className="text-gray-400 text-sm mb-3">Secure card payment via CMI (Visa / Mastercard).</p>
                <button
                  onClick={payWithCard}
                  disabled={cmiLoading}
                  data-testid="pay-card-btn"
                  className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                >
                  {cmiLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                  Pay by card
                </button>
              </div>
            </>
          )}

          {/* Hidden auto-submitting form to CMI gateway */}
          {cmiForm && (
            <form ref={formRef} action={cmiForm.gateway_url} method="POST" acceptCharset="UTF-8" data-testid="cmi-redirect-form">
              {Object.entries(cmiForm.form_fields).map(([name, value]) => (
                <input key={name} type="hidden" name={name} value={value} />
              ))}
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PaymentRedirectPage;
